import { Stack, type SxProps, type Theme, useTheme } from "@mui/material";
import { useNonPersistantMainStore, useSetFeatureFlagAsSeen } from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo, useState } from "react";
import { trackAiHome } from "../analytics";
import { GreetingsSection } from "../main-legacy/components/GreetingsSection";
import { AiHomeIntroModal } from "./components/AiHomeIntroModal";
import { ChatPanel } from "./components/ChatPanel";
import { CHAT_SIDEBAR_WIDTH, ChatSidebar } from "./components/ChatSidebar";
import {
  CHAT_SIDEBAR_RAIL_WIDTH,
  ChatSidebarRail,
} from "./components/ChatSidebarRail";
import { ChatStatChips } from "./components/ChatStatChips";
import { useAskHelenaConversationUrlSync } from "./hooks/useAskHelenaConversationUrlSync";
import {
  askHelenaConversationId,
  askHelenaMessages,
  hasOpenConversationAtom,
  isChatSidebarOpenAtom,
  openAskHelenaConversation,
  selectedConversationIdAtom,
  startNewChat,
} from "./store";

/**
 * Stands in for the live thread's id in the sidebar until the `conversation`
 * event lands — a first question is on screen before the service has named it.
 * The row's label falls back to the first user message, truncated, because the
 * service does not generate titles yet.
 */
const LIVE_CHAT_ID = "ask-helena-live";
const LIVE_TITLE_MAX_LENGTH = 40;

const AI_HOME_BANNER_FLAG = "show_ai_home_banner";

/**
 * One of the two sidebar states, stacked on the other. The hidden layer keeps
 * its intrinsic width — the wrapper clips it — and `visibility` takes it out of
 * the tab order and the accessibility tree while it is faded out.
 */
const sidebarLayerSx = (isVisible: boolean): SxProps<Theme> => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  opacity: isVisible ? 1 : 0,
  visibility: isVisible ? "visible" : "hidden",
  transition: (theme) =>
    theme.transitions.create(["opacity", "visibility"], {
      duration: theme.transitions.duration.shorter,
      // The incoming state waits for the width to open up under it.
      delay: isVisible ? 100 : 0,
    }),
});

/**
 * Helena's home screen — a chat shell with two states of the same layout: the
 * greeting + queue summary while no conversation is open, and the conversation
 * itself once one is.
 */
export const HomeAiChat = () => {
  const theme = useTheme();
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const liveConversationId = useAtomValue(askHelenaConversationId);
  const isSidebarOpen = useAtomValue(isChatSidebarOpenAtom);
  const messages = useAtomValue(askHelenaMessages);
  const hasOpenConversation = useAtomValue(hasOpenConversationAtom);

  const selectConversation = useSetAtom(openAskHelenaConversation);
  const newChat = useSetAtom(startNewChat);

  const featureFlags = useNonPersistantMainStore((state) => state.featureFlags);
  const { mutate: setFeatureAsSeen } = useSetFeatureFlagAsSeen();
  const [isIntroDismissed, setIsIntroDismissed] = useState(false);

  const isIntroModalOpen =
    Boolean(featureFlags?.[AI_HOME_BANNER_FLAG]) && !isIntroDismissed;

  const handleIntroClose = () => {
    setIsIntroDismissed(true);
    setFeatureAsSeen(AI_HOME_BANNER_FLAG);
  };

  // Mirrors the open conversation into `?conversation=<id>`, and restores it on
  // a reload or a shared link.
  useAskHelenaConversationUrlSync();

  const hasLiveConversation = messages.length > 0;

  // The thread on screen, listed for the sidebar. Once the list is refetched it
  // carries this conversation too, and the sidebar merges rather than duplicates
  // — handing the server's generated title back over this stand-in label.
  const liveTitle = messages[0]?.content ?? "";
  const currentSessionChat = useMemo(
    () =>
      hasLiveConversation
        ? {
            id: liveConversationId ?? LIVE_CHAT_ID,
            title:
              liveTitle.length > LIVE_TITLE_MAX_LENGTH
                ? `${liveTitle.slice(0, LIVE_TITLE_MAX_LENGTH)}…`
                : liveTitle,
            // The service has not named it yet, and a brand-new thread can
            // never already be pinned.
            rawTitle: null,
            isPinned: false,
          }
        : undefined,
    [hasLiveConversation, liveConversationId, liveTitle],
  );

  const activeRowId =
    liveConversationId ??
    selectedConversationId ??
    (hasLiveConversation ? LIVE_CHAT_ID : null);

  const handleNewChat = () => {
    trackAiHome("ai_new_chat_started", {
      trigger: "new_chat_button",
      previous_chat_id: liveConversationId ?? selectedConversationId,
    });
    newChat();
  };

  return (
    <Stack
      sx={{
        height: "100%",
        minHeight: 0,
        gap: "12px",
        backgroundColor: theme.palette.neutral.main,
        borderRadius: "12px",
        padding: "8px",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          p: "8px 16px",
        }}
      >
        <GreetingsSection />
        {hasOpenConversation && <ChatStatChips />}
      </Stack>

      <Stack
        direction="row"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.neutral["010"]}`,
          backgroundColor: theme.palette.neutral.main,
        }}
      >
        {/* Collapsing animates this wrapper's width; both states stay mounted
            and cross-fade inside it, so neither one pops in at the end. */}
        <Stack
          sx={{
            position: "relative",
            flexShrink: 0,
            minHeight: 0,
            overflow: "hidden",
            width: isSidebarOpen ? CHAT_SIDEBAR_WIDTH : CHAT_SIDEBAR_RAIL_WIDTH,
            borderRight: `1px solid ${theme.palette.neutral["010"]}`,
            transition: theme.transitions.create("width", {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
          }}
        >
          <Stack sx={sidebarLayerSx(isSidebarOpen)}>
            <ChatSidebar
              activeChatId={activeRowId}
              currentSessionChat={currentSessionChat}
              onSelectConversation={selectConversation}
              onNewChat={handleNewChat}
            />
          </Stack>
          <Stack sx={sidebarLayerSx(!isSidebarOpen)}>
            <ChatSidebarRail onNewChat={handleNewChat} />
          </Stack>
        </Stack>
        <ChatPanel />
      </Stack>

      <AiHomeIntroModal open={isIntroModalOpen} onClose={handleIntroClose} />
    </Stack>
  );
};
